import { Component, SimpleChanges, OnInit, OnDestroy } from '@angular/core';

import { map, Observable, take, Subject, takeUntil } from 'rxjs';
import { PbiTimeByColumnResult } from 'src/app/models/team-metrics';
import { PbiTimeByColumnCalculatorService } from 'src/app/services/pbi-time-by-column-calculator.service';
import { TeamMetricsService } from 'src/app/services/team-metrics.service';
import { MetricCardComponent } from '../metric-card/metric-card.component';
import { BaseMetricComponent } from './base-metric.component';
import { UserSettingsService } from 'src/app/services/user-settings-service';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { PbiColumnSettings } from 'src/app/models/pbi-column-settings';
import { PbiTimeByColumnChartRow } from 'src/app/models/pbi-time-by-column-chart-row';

/**
 * A metric component that visualizes the time PBIs spend in different board columns.
 * It features a custom bar chart with toggles to show or hide specific columns,
 * and it persists user settings for these toggles.
 */
@Component({
  selector: 'app-pbi-time-by-column-metric',
  standalone: true,
  imports: [MetricCardComponent, FormsModule, MatButtonModule, MatSlideToggleModule],
  template: `
    <app-metric-card
      title="PBI Time Spent by Column"
      icon="timeline"
      [isLoading]="isLoading || (isOtherMetricLoading ?? false)"
      [metricType]="'pbiTimeByColumn'"
      [metricData]="metricDataToDisplay"
      tooltipText="Average time work items spend in different columns throughout their lifecycle. This metric only considers PBIs/Bugs that have transitioned to the 'Done' state.">
    
      @if (allPossibleColumns.length > 0) {
        <div class="header-controls">
          <mat-slide-toggle
            [checked]="excludedColumns.length === 0 && allPossibleColumns.length > 0"
            (change)="toggleAllColumns()">
            <span class="toggle-all-label">Toggle All</span>
          </mat-slide-toggle>
          @if (totalPbisCount > 0) {
            <div class="total-pbis">
              Total PBIs: {{ totalPbisCount }}
            </div>
          }
        </div>
      }
    
      @if (displayData.length > 0) {
        <div class="custom-chart-container">
          @for (item of displayData; track item; let i = $index) {
            <div class="chart-row">
              <mat-slide-toggle
                [checked]="!isExcluded(item.column)"
                (change)="toggleColumn(item.column)"
                class="column-toggle">
              </mat-slide-toggle>
              <div class="column-label" [title]="item.column">
                <span class="color-box" [style.background-color]="getColor(item.column)"></span>
                {{ truncate(item.column, 15) }}
              </div>
              <div class="bar-container">
                <div class="bar" [title]="item.column" [style.width.%]="item.animatedPercentage" [style.background-color]="getColor(item.column)"></div>
              </div>
              <div class="time-label">{{ item.time }} ({{ item.percentage }}%)</div>
            </div>
          }
        </div>
      }
      @if (!isLoading && displayData.length === 0) {
        <div class="kpi-content">
          <div class="kpi-value">{{ metricDataToDisplay?.subtitle || 'N/A' }}</div>
        </div>
      }
      @if (!metricDataToDisplay && !isLoading) {
        <div class="kpi-content">
          <div class="kpi-value">N/A</div>
        </div>
      }
    </app-metric-card>
    `,
  styles: [`
    :host { display: block; height: 100%; margin-bottom: 20px; }
    :host ::ng-deep .kpi-card { border-right: 4px solid #0093a1; }
    .header-controls { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .toggle-all-label { margin-left: 3px; color: #505050; font-weight: 500; }
    .total-pbis { font-size: 1.3em; font-weight: bold; color: #555; }
    .custom-chart-container { display: flex; flex-direction: column; gap: 12px; padding-top: 10px; width: 100%; }
    .chart-row { display: grid; grid-template-columns: auto 150px 1fr 110px; align-items: center; gap: 10px; }
    .column-toggle { margin-right: 10px; }
    .column-label { display: flex; align-items: center; white-space: nowrap; color: #333; overflow: hidden; text-overflow: ellipsis; }
    .color-box { width: 12px; height: 12px; margin-right: 8px; border-radius: 2px; flex-shrink: 0; }
    .bar-container { background-color: #f0f0f0; border-radius: 6px; height: 15px; overflow: hidden; }
    .bar { height: 100%; border-radius: 6px; }
    .time-label { text-align: right; font-size: 1em; font-weight: bold; color: #555; white-space: nowrap; }
    .kpi-content { display: flex; justify-content: center; align-items: center; height: 100%; }
    .kpi-value { font-size: 1.5rem; font-weight: bold; color: #888; }
  `]
})
export class PbiTimeByColumnMetricComponent extends BaseMetricComponent<PbiTimeByColumnResult> implements OnInit, OnDestroy {
  public allPossibleColumns: string[] = [];
  public excludedColumns: string[] = [];
  public displayData: PbiTimeByColumnChartRow[] = [];
  public totalPbisCount: number = 0;
  private fullMetricData: PbiTimeByColumnResult | null = null;
  private animationFrameId: number | null = null;
  private readonly settingsChanged = new Subject<void>();

  /**
   * @param pbiTimeByColumnCalculatorService The service used to calculate the metric data.
   * @param userSettingsService Service for persisting and retrieving user-specific settings.
   * @param teamMetricsService The core service for team metrics, used by the base component.
   */
  constructor(
    private readonly pbiTimeByColumnCalculatorService: PbiTimeByColumnCalculatorService,
    private readonly userSettingsService: UserSettingsService,
    teamMetricsService: TeamMetricsService
  ) {
    super(teamMetricsService);
  }

  /**
   * Initializes the component, setting up a debounced subscription to save column
   * visibility settings whenever they are changed by the user.
   */
  override ngOnInit(): void {
    super.ngOnInit();
    this.settingsChanged.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.saveColumnSettings();
    });
  }

  /**
   * Angular lifecycle hook that detects changes to input properties.
   * By overriding ngOnChanges and not calling the base implementation, this component
   * takes full control of its data fetching lifecycle. This is necessary to prevent a 
   * race condition where metric data could be fetched before user-specific column 
   * settings are loaded.
   * @param changes An object of changed properties.
   */
  override ngOnChanges(changes: SimpleChanges): void {
    if ((changes['team'] || changes['startDate'] || changes['endDate']) && this.team) {
      this.loadColumnSettings();
    }
    
    if (changes['isOtherMetricLoading'] && !changes['isOtherMetricLoading'].firstChange) {
      this.trySetCardData();
    }
  }

  /**
   * Loads the user's saved column visibility settings for the current team
   * from the `UserSettingsService`.
   */
  private loadColumnSettings(): void {
    if (!this.team) return;
    this.userSettingsService.ensureSettings().pipe(take(1)).subscribe(settings => {
      const pbiSettings = settings?.getSetting<PbiColumnSettings>('pbiColumnSettings') ?? {};
      this.excludedColumns = pbiSettings[this.team!] ?? [];
      this.fetchMetric();
    });
  }

  /**
   * Saves the current set of excluded columns for the team to the `UserSettingsService`.
   */
  private saveColumnSettings(): void {
    if (!this.team) return;
    this.userSettingsService.ensureSettings().pipe(take(1)).subscribe(settings => {
      const pbiSettings = settings?.getSetting<PbiColumnSettings>('pbiColumnSettings') ?? {};
      pbiSettings[this.team!] = this.excludedColumns;
      settings?.setSetting('pbiColumnSettings', pbiSettings);
    });
  }

  /**
   * Checks if a given column is currently in the excluded list.
   * @param column The name of the column to check.
   * @returns `true` if the column is excluded, `false` otherwise.
   */
  public isExcluded(column: string): boolean {
    return this.excludedColumns.includes(column);
  }

  /**
   * Toggles the visibility of a column. Adds or removes the column from the
   * `excludedColumns` list and triggers a settings save and data refetch.
   * @param column The name of the column to toggle.
   */
  public toggleColumn(column: string): void {
    if (this.isExcluded(column)) {
      this.excludedColumns = this.excludedColumns.filter(c => c !== column);
    } else {
      this.excludedColumns.push(column);
    }
    this.updateDisplayData(true);
    this.settingsChanged.next();
  }

  /**
   * Resets the column visibility, showing all columns, and triggers a data refetch.
   */
  public toggleAllColumns(): void {
    if (this.excludedColumns.length === 0) {
      this.excludedColumns = [...this.allPossibleColumns];
    } else {
      this.excludedColumns = [];
    }
    this.updateDisplayData(true);
    this.settingsChanged.next();
  }

  /**
   * Determines the color for a chart bar and its corresponding elements based on its
   * position in the active list and whether it's excluded.
   * @param column The name of the column.
   * @returns A CSS color string.
   */
  public getColor(column: string): string {
    if (this.isExcluded(column)) {
      return '#e0e0e0';
    }

    const stableIndex = this.allPossibleColumns.findIndex(c => c === column);
    if (stableIndex === -1) {
      return '#e0e0e0';
    }

    const totalColumns = this.allPossibleColumns.length;
    const p = totalColumns > 1 ? stableIndex / (totalColumns - 1) : 0;

    const keyHues = [0, 50, 120];
    const keyPositions = [0, 0.5, 1];

    let hue: number;
    if (p < keyPositions[1]) {
      const t = p / keyPositions[1];
      hue = keyHues[0] * (1 - t) + keyHues[1] * t;
    } else {
      const t = (p - keyPositions[1]) / (1 - keyPositions[1]);
      hue = keyHues[1] * (1 - t) + keyHues[2] * t;
    }

    const saturation = 90;
    const lightness = 50 - 30 * Math.abs(p - 0.5);

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  /**
   * Truncates a string to a specified length, appending an ellipsis if it exceeds the limit.
   * @param value The string to truncate.
   * @param limit The maximum length of the string.
   * @param ellipsis The string to append if truncated.
   * @returns The truncated string.
   */
  public truncate(value: string, limit = 25, ellipsis = '...'): string {
    return value.length > limit ? value.substring(0, limit) + ellipsis : value;
  }

  /**
   * Implements the abstract method from the base class to fetch the metric data.
   * @param team The name of the team.
   * @param isoStartDate The start date of the reporting period.
   * @param isoEndDate The end date of the reporting period.
   * @param org The ADO organization name.
   * @param project The ADO project name.
   * @returns An Observable that emits the `PbiTimeByColumnResult`.
   */
  protected callFetchMetric(
    team: string,
    isoStartDate: string,
    isoEndDate: string,
    org: string,
    project: string
  ): Observable<PbiTimeByColumnResult> {
    return this.pbiTimeByColumnCalculatorService.calculate(team, isoStartDate, isoEndDate, org, project).pipe(
      map(result => {
        this.fullMetricData = result;
        return result ?? { 
          value: 0, 
          displayValue: 'N/A', 
          subtitle: 'No PBI data available for this period.', 
          columns: [], 
          totalPbis: 0, 
          allColumnsWithData: [] 
        };
      })
    );
  }

  /**
   * Overrides the base class method to process the fetched data, updating the
   * total PBI count and preparing the data for display.
   */
  protected override trySetCardData(): void {
    super.trySetCardData();
    if (this.fullMetricData) {
      this.totalPbisCount = this.fullMetricData.totalPbis;
      if (this.fullMetricData.allColumnsWithData) {
        this.allPossibleColumns = this.fullMetricData.allColumnsWithData;
      }
      if (!this.isOtherMetricLoading) {
        this.updateDisplayData(true);
      }
    } else {
      this.displayData = [];
      this.totalPbisCount = 0;
    }
  }

  /**
   * Updates the `displayData` array, which drives the chart rendering. It ensures
   * that all possible columns are represented, even if they have no data for the
   * current period.
   * @param animate A boolean indicating whether the bar widths should be animated.
   */
  private updateDisplayData(animate: boolean = true): void {
    if (!this.fullMetricData) {
      this.displayData = [];
      return;
    }

    const activeColumns = this.fullMetricData.columns.filter(c => !this.isExcluded(c.column));
    const activeColumnNames = new Set(activeColumns.map(s => s.column));
    const visibleTotalTime = activeColumns.reduce((sum, col) => sum + col.time, 0);
    const displayDataMap = new Map(this.displayData.map(d => [d.column, d]));

    const combinedData: PbiTimeByColumnChartRow[] = this.allPossibleColumns.map(column => {
      const existingItem = displayDataMap.get(column);
      const animatedPercentage = existingItem ? existingItem.animatedPercentage : 0;

      if (activeColumnNames.has(column)) {
        const columnData = activeColumns.find(s => s.column === column)!;
        const percentage = visibleTotalTime > 0 ? (columnData.time / visibleTotalTime) * 100 : 0;
        return {
          column: column,
          time: `${columnData.time.toFixed(1)}d`,
          percentage: Math.round(percentage),
          animatedPercentage
        };
      } else {
        return {
          column: column,
          time: 'N/A',
          percentage: 0,
          animatedPercentage
        };
      }
    });

    this.displayData = combinedData;
    if (animate) {
      this.animateBars();
    }
  }

  /**
   * Animates the width of the bars in the chart from their current width to the
   * target width using `requestAnimationFrame` for a smooth transition.
   */
  private animateBars(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    const duration = 1000;
    let startTime: number | null = null;

    const animationStep = (timestamp: number) => {
      startTime ??= timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      this.displayData.forEach(item => {
        const start = item.animatedPercentage;
        const end = item.percentage;
        item.animatedPercentage = start + (end - start) * progress;
      });

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animationStep);
      } else {
        this.displayData.forEach(item => item.animatedPercentage = item.percentage);
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(animationStep);
  }

  override ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    super.ngOnDestroy();
  }
}
